import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { getLocale } from '../utils/AsyncIntlProvider';
import { useRenderingOptions } from '../utils/useRenderingOptions';
import { RenderingOptions } from '../types';
import { useTelemetry } from '@snowflake/core-ui';
import { SignupEventName } from '../utils/SignupLogger';
import { getBotDetectionProps } from '../utils/botDetection';
import { ABExperimentName, DEFAULT_GROUP, SignupEventType, isCortexCodeFlow } from '@signup/shared';
import {
  getClientVersion,
  getCoordinates,
  getExperimentGroup,
  getExperimentGroups,
  getFeatureFlags,
  getIsoCode,
  getReferrerUrl,
} from '../utils/configurations';
import { useSignupFlowType } from '../utils/useSignupFlowType';

interface StateVariable<T> {
  value: T;
  setValue: (value: T) => void;
}

interface SignupPageState {
  formSubmitted: StateVariable<boolean>;
  locale: StateVariable<string>;
  docsLocale: StateVariable<string>;
  renderingOptions: RenderingOptions;
  modifiedSignupFlowExperimentGroup: string;
}

interface props {
  children: ReactNode;
}

//locales used in the product are different from those in the docs
//the mapping is used to localize the docs links
const getDocsLocale = (locale: string) => {
  switch (locale) {
    case 'en-US':
      return 'en';
    case 'fr-FR':
      return 'fr';
    case 'ja':
      return 'ja';
    case 'ko':
      return 'ko';
    default:
      return 'en';
  }
};

const SignupPageContext = createContext<SignupPageState>({} as SignupPageState);

export const SignupPageProvider = ({ children }: props) => {
  //initialize the state
  const signupFlowType = useSignupFlowType();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [locale, setLocale] = useState(getLocale());
  const [docsLocale, setDocsLocale] = useState(getDocsLocale(getLocale()));
  const renderingOptions = useRenderingOptions(locale);
  const { logEvent } = useTelemetry();

  const backendExperimentGroup = useMemo(
    () => getExperimentGroup(ABExperimentName.MODIFIED_SIGNUP_FLOW),
    [],
  );

  const userCountry = useMemo(() => getIsoCode(), []);

  const modifiedSignupFlowExperimentGroup = useMemo(() => {
    // Cortex Code flows (CortexCodeCLI and CocoSubscriptionPromo) always get the modified flow,
    // regardless of region
    if (isCortexCodeFlow(signupFlowType)) {
      return 'MODIFIED_FLOW_TREATMENT';
    }

    const isUSUser = userCountry === 'US';
    // If non-US user was assigned to treatment/control, force them to DEFAULT_GROUP
    if (!isUSUser && backendExperimentGroup !== DEFAULT_GROUP) {
      return DEFAULT_GROUP;
    }

    return backendExperimentGroup;
  }, [backendExperimentGroup, userCountry, signupFlowType]);

  // only calls once.
  React.useEffect(() => {
    const isModifiedSignupFlow = modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT';

    const baseEventData = {
      signupFlowType,
      clientVersion: getClientVersion(),
      isoCode: getIsoCode(),
      coordiates: getCoordinates(),
      locale,
      docsLocale,
      experimentGroups: getExperimentGroups(),
      featureFlags: getFeatureFlags(),
      referralUrl: getReferrerUrl(),
    };

    // Log general rendering event
    logEvent({
      event: SignupEventName.SIGNUP_FORM_RENDERING,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: baseEventData,
    });

    logEvent({
      event: SignupEventName.SIGNUP_BOT_DETECTION,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: { ...getBotDetectionProps(), trigger: 'page_load' },
    });

    // Log specific modified flow rendering event if user is in treatment
    if (isModifiedSignupFlow) {
      logEvent({
        event: SignupEventName.SIGNUP_MODIFIED_FLOW_RENDERING,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: {
          ...baseEventData,
          treatmentGroup: modifiedSignupFlowExperimentGroup,
        },
      });
    }
  }, []);

  const initialState: SignupPageState = useMemo(
    () => ({
      formSubmitted: {
        value: formSubmitted,
        setValue: setFormSubmitted,
      },
      locale: {
        value: locale,
        setValue: setLocale,
      },
      docsLocale: {
        value: docsLocale,
        setValue: setDocsLocale,
      },
      renderingOptions,
      modifiedSignupFlowExperimentGroup,
    }),
    [renderingOptions, formSubmitted, locale, docsLocale, modifiedSignupFlowExperimentGroup],
  );
  return <SignupPageContext.Provider value={initialState}>{children}</SignupPageContext.Provider>;
};

export const useSignupPageContext = () => {
  return useContext<SignupPageState>(SignupPageContext);
};
